import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getDataWAuth, postDataWAuth, deleteDataWAuth } from "../../utils/api-service";
import {
  APP_ACTIONS,
  APP_VIEW,
  DIRECT_DEBIT_CUSTOMER_DATA,
  ADD_DIRECT_DEBIT_CUSTOMER_DATA,
  DELETE_DIRECT_DEBIT_CUSTOMER_DATA,
} from "../../constants/constant";
import ModalMessageFunction from "../../common/modal-message-function";
import ToastMessageFunction from "../../common/toast-message-function";
import { useTranslation } from "../../common/translation-context";
import { checkErrorMsg, getUserRole } from "../../utils/utils";
import DMGrid from "../../common/data-grid/dmGrid";

const DirectDebitDealers = () => {
  const { translate: t } = useTranslation();
  const userData = useSelector((state) => state.userState.userData);
  const [directDebitDealers, setDirectDebitDealers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionPerformed, setActionPerformed] = useState(false);
  const [requestType, setRequestType] = useState("");
  const [dealerId, setDealerID] = useState(-1);
  
  const processAction = (action, requestType) => {
    setActionPerformed(action);
    setRequestType(requestType);
  };

  const modalManager = ModalMessageFunction(processAction);
  const toastManager = ToastMessageFunction();
  const showToast = useCallback(
    (message, severity) => {
      toastManager.getToast(message, severity);
    },
    [toastManager]
  );

  const getModalAction = (requestType, requestStatus) => {
    modalManager.getModal(requestType, requestStatus);
  };

  // Fetch all direct debit dealers
  const fetchDirectDebitDealers = async () => {
    setLoading(true);
    try {
      const response = await getDataWAuth(DIRECT_DEBIT_CUSTOMER_DATA);
      setDirectDebitDealers(response.data || []);
    } catch (error) {
      if (checkErrorMsg(error)) showToast(checkErrorMsg(error), "error");
      else showToast(t("failFetchDirectDebitDealers"), "error");
    } finally {
      setLoading(false);
    }
  };

  // Delete direct debit dealer
  const deleteDealerHandler = async (dealerId) => {
    setActionLoading(true);
    try {
      await deleteDataWAuth(DELETE_DIRECT_DEBIT_CUSTOMER_DATA, { id: dealerId });
      showToast(t("deleteDirectDebitDealerSuccess"), "success");
      fetchDirectDebitDealers();
    } catch (error) {
      if (checkErrorMsg(error)) showToast(checkErrorMsg(error), "error");
      else showToast(t("failDeleteDirectDebitDealer"), "error");
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    if (actionPerformed && requestType === "deleteDirectDebitDealer") {
      deleteDealerHandler(dealerId);
    }
    setActionPerformed(false);
    setRequestType("");
  }, [actionPerformed, dealerId]);

  const deleteDirectDebitDealer = (dealerId) => {
    setDealerID(dealerId);
    getModalAction(APP_ACTIONS.DELETEDIRECTDEBITDEALER, "deleteDirectDebitDealer");
  };

  // Create direct debit dealer
  const createDirectDebitDealer = async (values) => {
    setActionLoading(true);
    try {
      const payload = {
        directDebitCustomerNumber: values.directDebitCustomerNumber,
        currency: values.currency,
        country: values.country,
        modifiedBy: userData?.username || "SYSTEM",
        modifiedDate: new Date().toISOString()
      };
      
      await postDataWAuth(ADD_DIRECT_DEBIT_CUSTOMER_DATA, payload);
      showToast(t("addDirectDebitDealerToast"), "success");
      fetchDirectDebitDealers();
    } catch (error) {
      const errorMsg = checkErrorMsg(error) || t("failAddDirectDebitDealer");
      showToast(errorMsg, "error");
      // Re-throw the error so the grid can handle it
      throw new Error(errorMsg);
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    fetchDirectDebitDealers();
  }, []);

  return (
    <>
      {modalManager.modal}
      {toastManager.toast}

      <DMGrid
        role={getUserRole(userData)}
        id="directDebitTable"
        enableEditing={true}
        editDisplayMode="row"
        isLoaderVisible={loading || actionLoading}
        view={APP_VIEW.DM}
        dmView="directDebitDealers"
        row={directDebitDealers}
        header="directDebitDealers"
        count={directDebitDealers?.length}
        buttonText="addDirectDebitDealer"
        create={createDirectDebitDealer}
        delete={deleteDirectDebitDealer}
      />
    </>
  );
};

export default DirectDebitDealers;